import { useCallback, useMemo } from 'react'
import { alphabeticalSort } from '@cims-online/shared'
import { useUserSelectLabel } from 'components/CIMSSelect/common/UserSelect/hooks/useUserSelectLabel'
import { useUserSelectTooltip } from 'components/CIMSSelect/common/UserSelect/hooks/useUserSelectTooltip'
import { useUserSelectContext } from 'components/CIMSSelect/common/UserSelect/UserSelectContext'
import type { LabeledValueType } from 'components/CIMSSelect/types'

/**
 * Adapts project-user select data for approver tags and persisted user IDs.
 */
export function useFieldUsers() {
  const { fromLabeledValues, users } = useUserSelectContext()
  const label = useUserSelectLabel()
  const tooltip = useUserSelectTooltip()
  const usersById = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users]
  )
  const userLabel = useCallback(
    (id: string) => {
      const user = usersById.get(id)
      return user ? label(user) : `Unknown user (${id})`
    },
    [label, usersById]
  )
  const userTooltip = useCallback(
    (id: string) => {
      const user = usersById.get(id)
      return user ? tooltip(user) ?? undefined : 'This user could not be found.'
    },
    [tooltip, usersById]
  )
  const toApproverValues = useCallback(
    (ids: string[]): LabeledValueType[] =>
      ids
        .map((id) => {
          const user = usersById.get(id)
          if (!user)
            return {
              deleted: true,
              label: `Unknown user (${id})`,
              tooltip: 'This user could not be found.',
              value: id,
            }

          return {
            label: label(user),
            tooltip: tooltip(user) ?? undefined,
            value: user.id,
          }
        })
        .sort(alphabeticalSort('label')),
    [label, tooltip, usersById]
  )

  return {
    fromLabeledValues,
    toApproverValues,
    userLabel,
    usersById,
    userTooltip,
  }
}
